import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { StorageItems } from '../../shared/constants/app';
import { VerifyToken } from '../../shared/constants/MFA';
import {
  addItemToLocalstorage,
  removeItemFromLocalstorage,
} from '../common/storage';
import { setTokenData } from '../common/token';
import { userStore } from '../common/userStore';
import { useMfaModalContext } from '../../shared/providers/MfaModalProvider';
import useTracking from '../hooks/useTracking';
import { LoginSuccessData, self } from '../../shared/service/auth';
import { getTeamsAndCountries } from '../../shared/service/data';
import { SelfData } from '../../shared/constants/user';

interface ErrorDetails {
  message: string;
}

interface DoLoginResult {
  success: boolean;
  error?: ErrorDetails;
}

export function useLogin() {
  const { allowAccess } = useMfaModalContext();
  const { t } = useTranslation();
  const { user: AnalyticsUser } = useTracking();
  const navigate = useNavigate();

  const setupUserProfileForAnalytics = (data: SelfData) => {
    const { id, createdAt, updatedAt, memberships } = data;
    const organisationId = memberships[0]?.organisationTeam.organisationId;

    AnalyticsUser.initialize(id);
    AnalyticsUser.setProperties(
      {
        userId: id,
        created: createdAt,
        updated: updatedAt,
        ...(organisationId !== undefined && {
          organisation: organisationId.toString(),
        }),
      },
      'once',
    );
  };

  const hasDashboardAccess = async (data: SelfData) => {
    if (data.isStaff) return true;

    const { showOrganisationDropdown, teams } = await getTeamsAndCountries();
    return showOrganisationDropdown || teams.length > 0;
  };

  const applyHrUser = (data: SelfData) => {
    const memberships = (data.memberships ?? []).map((membership) => {
      return {
        id: membership.id,
        role: membership.role,
        status: membership.status,
        organisationTeamId: membership.organisationTeamId,
        organisationTeam: {
          id: membership.organisationTeam.id,
          name: membership.organisationTeam.name,
          organisationId: membership.organisationTeam.organisationId,
          organisation: {
            name: membership.organisationTeam.organisation.name,
            id: membership.organisationTeam.organisationId,
            status: membership.organisationTeam.organisation.status,
          },
        },
      };
    });
    const user = { ...data, memberships };

    userStore.set(user);
    addItemToLocalstorage(StorageItems.SESSION_HINT, '1');
    setupUserProfileForAnalytics(user);
    navigate('/d/dimensions');
  };

  const nonHrError: DoLoginResult = {
    success: false,
    error: {
      message:
        t('ssoTokenVerifyFailedHeading') ??
        'Authentication failed due to non-HR role.',
    },
  };

  const doLogin = async (data: SelfData): Promise<DoLoginResult> => {
    if (!(await hasDashboardAccess(data))) return nonHrError;
    if (allowAccess.login) applyHrUser(data);
    return { success: true };
  };

  const completeSSOLogin = async (
    tokens: LoginSuccessData,
  ): Promise<DoLoginResult> => {
    setTokenData({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: tokens.expiresAt,
    });

    try {
      const response = await self().catch((e: any) => {
        if (e?.data?.override?.code !== VerifyToken.INVALID_ACCESS_TOKEN) {
          throw e;
        }
        removeItemFromLocalstorage(StorageItems.ACCESS_TOKEN);
        return self();
      });
      const data = response.data;
      if (!(await hasDashboardAccess(data))) return nonHrError;
      applyHrUser(data);
      return { success: true };
    } catch (e) {
      return {
        success: false,
        error: {
          message: t('ssoTokenVerifyFailedHeading') ?? 'Authentication failed.',
        },
      };
    }
  };

  return { doLogin, completeSSOLogin };
}
